"use client";

import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, usePathname } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { motion, AnimatePresence } from "motion/react";

const localeConfig: Record<string, { flag: string; label: string; name: string }> = {
  en: { flag: "/assets/flags/gb.svg", label: "EN", name: "English" },
  de: { flag: "/assets/flags/de.svg", label: "DE", name: "Deutsch" },
  no: { flag: "/assets/flags/no.svg", label: "NO", name: "Norsk" },
};

type LanguageSwitcherProps = {
  direction?: "down" | "up";
};

const LanguageSwitcher = ({ direction = "down" }: LanguageSwitcherProps) => {
  const locale = useLocale();
  const t = useTranslations("nav");
  const tA11y = useTranslations("accessibility");
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const others = routing.locales.filter((l) => l !== locale);
  const current = localeConfig[locale] ?? localeConfig.en;

  // German-only pages that should redirect to home for other locales
  const deOnlyPages = ["/imprint"];

  const switchLocale = (next: string) => {
    setOpen(false);
    const target = next !== "de" && deOnlyPages.includes(pathname) ? "/" : pathname;
    router.replace(target, { locale: next });
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const getOptionY = (i: number) => {
    const offset = 52 + i * 46;
    return direction === "up" ? -offset : offset;
  };

  const labelPosition =
    direction === "up"
      ? "right-[calc(100%+8px)]"
      : "left-[calc(100%+8px)]";

  const labelSlide =
    direction === "up"
      ? "translate-x-2 group-hover/opt:-translate-x-0"
      : "-translate-x-2 group-hover/opt:translate-x-0";

  const tooltipPosition =
    direction === "up"
      ? "right-[calc(100%+8px)]"
      : "left-[calc(100%+8px)]";

  const tooltipSlide =
    direction === "up"
      ? "translate-x-2 group-hover:-translate-x-0"
      : "-translate-x-2 group-hover:translate-x-0";

  return (
    <div ref={ref} className="relative flex items-center">
      {/* Main flag trigger */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-[44px] h-[44px] md:w-[38px] md:h-[38px] lg:w-[44px] lg:h-[44px] rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-105 group z-10"
        aria-label={t("switchLanguage")}
        aria-expanded={open}
      >
        {/* Solid border ring */}
        <span className="absolute inset-0 rounded-full border border-white/[0.12] transition-all duration-300 group-hover:border-white/[0.2]" />

        {/* Rotating light beam */}
        <span className="absolute inset-[-1px] rounded-full overflow-hidden pointer-events-none">
          <motion.span
            className="absolute inset-[-50%]"
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            style={{
              background: "conic-gradient(from 270deg, transparent 0deg, rgba(135,82,250,0.8) 30deg, transparent 60deg)",
            }}
          />
          <span className="absolute inset-[1.5px] rounded-full bg-[#0A001F] transition-all duration-300 group-hover:bg-[#0A001F]/80 group-hover:backdrop-blur-sm group-hover:shadow-[inset_0_0_12px_rgba(135,82,250,0.08)]" />
        </span>

        {/* Flag */}
        <span className="relative w-[30px] h-[30px] md:w-[24px] md:h-[24px] lg:w-[30px] lg:h-[30px] rounded-full overflow-hidden">
          <Image
            src={current.flag}
            alt={current.name}
            aria-hidden="true"
            width={60}
            height={45}
            className="absolute inset-0 w-full h-full object-cover scale-[1.12] transition-opacity duration-300 group-hover:opacity-70"
          />
        </span>

        {/* Tooltip — hover only, hidden when open */}
        {!open && (
          <span className={`absolute ${tooltipPosition} whitespace-nowrap px-[10px] py-[4px] rounded-[8px] bg-[#121124]/90 border border-white/[0.08] backdrop-blur-xl text-[12px] font-medium text-white/60 opacity-0 ${tooltipSlide} pointer-events-none transition-all duration-200 group-hover:opacity-100`}>
            {t("switchLanguage")}
          </span>
        )}
      </button>

      {/* Stacked circle options */}
      <AnimatePresence>
        {open && others.map((l, i) => {
          const cfg = localeConfig[l];
          return (
            <motion.button
              key={l}
              initial={{ opacity: 0, y: 0, scale: 0.5 }}
              animate={{ opacity: 1, y: getOptionY(i), scale: 1 }}
              exit={{ opacity: 0, y: 0, scale: 0.5 }}
              transition={{
                duration: 0.25,
                delay: i * 0.06,
                ease: [0.22, 1, 0.36, 1],
              }}
              onClick={() => switchLocale(l)}
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[38px] h-[38px] md:w-[34px] md:h-[34px] lg:w-[38px] lg:h-[38px] rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 group/opt z-[5]"
              aria-label={tA11y('switchToLanguage', { language: cfg.name })}
            >
              {/* Border */}
              <span className="absolute inset-0 rounded-full border border-white/[0.1] bg-[#0A001F] transition-all duration-200 group-hover/opt:border-white/[0.25] group-hover/opt:shadow-[0_0_12px_rgba(135,82,250,0.15)]" />

              {/* Flag */}
              <span className="relative w-[26px] h-[26px] md:w-[22px] md:h-[22px] lg:w-[26px] lg:h-[26px] rounded-full overflow-hidden">
                <Image
                  src={cfg.flag}
                  alt={cfg.name}
                  aria-hidden="true"
                  width={52}
                  height={39}
                  className="absolute inset-0 w-full h-full object-cover scale-[1.12] transition-opacity duration-200 group-hover/opt:opacity-80"
                />
              </span>

              {/* Label — visible on hover only */}
              <span className={`absolute ${labelPosition} whitespace-nowrap px-[10px] py-[4px] rounded-[8px] bg-[#121124]/90 border border-white/[0.08] backdrop-blur-xl text-[12px] font-medium text-white/70 opacity-0 ${labelSlide} pointer-events-none transition-all duration-200 group-hover/opt:opacity-100`}>
                {cfg.name}
              </span>
            </motion.button>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
